var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Chip, Grid } from "@mui/material";
import { PageContainer, PageContent } from "../../components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { FCard } from "@ui/components/card";
import { PrimaryGrid, GridFilters, GridCellStack } from "../../components/grid";
import { StatusIndicator } from "@ui/components/status";
import { SignalMeter } from "@ui/components/icons/SignalMeter";
import { useDashboardFilter } from "./hooks/useDashboardFilter";
import { useDashboardActions } from "./hooks/useDashboardActions";
import { useTranslation } from "react-i18next";
import { colCenter } from "@ui/components/style/style";
import { SButton } from "@ui/components/buttons/SButton";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { MaintenanceRequestsDialog } from "./components/MaintenanceRequestsDialog";
import { DashboardDeviceCard } from "./components/DashboardDeviceCard";
import localStorageAccessor from "@utility/local-storage-accessor";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { getAllAccessPoints, getAccessPointsStatistics, } from "@reducers/access-points/thunk/access-point-thunk";
import { SIconButton } from "@ui/components/buttons";
import MaintenanceIcon from "@ui/components/icons/MaintenanceIcon";
import { Icon } from "@iconify/react";
import { userService } from "@utility/user-service";
import { clientsApi } from "@api/enpoints/clients-api";
import { categoriesApi } from "@api/enpoints/categories-api";
const toOptions = (rows) => (rows !== null && rows !== void 0 ? rows : []).map((r) => ({ value: r.id, label: r.name }));
const DashboardPage = () => {
    var _a, _b;
    const { t } = useTranslation(["form", "validation", "common"]);
    // Maintenance requests dialog state
    const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
    const [selectedApForMaintenance, setSelectedApForMaintenance] = useState(null);
    // Delete AP dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [apToDelete, setApToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const dispatch = useAppDispatch();
    const isSuperAdmin = userService.hasRole("Super Admin");
    const { currentPageNumber, currentPageSize, sortColumn, sortDirection, filters: storeFilters, } = useAppSelector((state) => state.accessPoint);
    useEffect(() => {
        localStorageAccessor.removeSelectedAccessPoint();
    }, []);
    const { devices, loading, searchTerm, filters, paginationInfo, statistics, activeFilter, selectedCard, handleFilterChange, handleSearchChange, handleActiveFilterChange, handlePageChange, handlePageSizeChange, } = useDashboardFilter();
    const refetch = useCallback(() => {
        dispatch(getAllAccessPoints({
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn: sortColumn !== null && sortColumn !== void 0 ? sortColumn : undefined,
            sortDirection: sortDirection !== null && sortDirection !== void 0 ? sortDirection : undefined,
            filters: Object.assign(Object.assign({}, storeFilters), { isActive: activeFilter !== null ? activeFilter : undefined }),
        }));
        if (isSuperAdmin) {
            dispatch(getAccessPointsStatistics());
        }
    }, [
        dispatch,
        currentPageNumber,
        currentPageSize,
        sortColumn,
        sortDirection,
        storeFilters,
        activeFilter,
        isSuperAdmin,
    ]);
    const { handleDeleteAccessPoint, handleViewNodes, handleSettings } = useDashboardActions(refetch);
    // Build stats cards from real statistics
    const statsCards = useMemo(() => {
        var _a, _b, _c;
        return [
            {
                id: "active-devices",
                title: t("common:dashboard.activeDevices"),
                value: (_a = statistics === null || statistics === void 0 ? void 0 : statistics.activeCount) !== null && _a !== void 0 ? _a : 0,
                filterValue: true,
            },
            {
                id: "inactive-devices",
                title: t("common:dashboard.inactiveDevices"),
                value: (_b = statistics === null || statistics === void 0 ? void 0 : statistics.inactiveCount) !== null && _b !== void 0 ? _b : 0,
                filterValue: false,
            },
            {
                id: "all-devices",
                title: t("common:dashboard.allDevices"),
                value: (_c = statistics === null || statistics === void 0 ? void 0 : statistics.totalCount) !== null && _c !== void 0 ? _c : 0,
                filterValue: null,
            },
        ];
    }, [statistics, t]);
    const handleCardClick = (cardId, filterValue) => {
        // Clicking the selected card toggles back to the Active default.
        handleActiveFilterChange(selectedCard === cardId ? true : filterValue);
    };
    const handleMaintenanceDialogClose = () => {
        setMaintenanceDialogOpen(false);
        setSelectedApForMaintenance(null);
    };
    const handleDeleteDialogClose = () => {
        if (!deleteLoading) {
            setDeleteDialogOpen(false);
            setApToDelete(null);
            setDeleteError(null);
        }
    };
    const handleConfirmDeleteAp = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!apToDelete)
            return;
        setDeleteError(null);
        setDeleteLoading(true);
        try {
            yield handleDeleteAccessPoint(apToDelete.deviceId);
            handleDeleteDialogClose();
        }
        catch (err) {
            setDeleteError((err === null || err === void 0 ? void 0 : err.message) || ((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || "Failed to delete");
        }
        finally {
            setDeleteLoading(false);
        }
    });
    const columns = useMemo(() => [
        {
            field: "ap",
            headerName: t("common:grid.ap"),
            minWidth: 80,
            filterable: false,
            renderCell: (params) => (React.createElement(Box, { sx: { display: "flex", justifyContent: "center" } },
                React.createElement(SignalMeter, { width: 66, variant: params.row.status === "active" ? "success" : "warning", height: 57 }))),
        },
        {
            field: "nameId",
            headerName: t("common:grid.nameId"),
            flex: 3,
            minWidth: 200,
            filterable: true,
            filterType: "text",
            renderCell: (params) => (React.createElement(GridCellStack, { topText: params.row.name, bottomText: params.row.deviceId, topStyle: { fontWeight: 500 }, bottomVariant: "caption", bottomColor: "text.secondary" })),
        },
        {
            field: "nodeCount",
            headerName: t("common:grid.nodeCount"),
            flex: 2,
            minWidth: 120,
            filterable: false,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (React.createElement(Chip, { label: `${params.row.nodeCount} ${t("common:grid.devices")}`, size: "small", variant: "soft", color: "default" })),
        },
        {
            field: "status",
            headerName: t("common:grid.status"),
            flex: 2,
            minWidth: 120,
            filterable: false,
            renderCell: (params) => (React.createElement(StatusIndicator, { variant: params.row.status })),
        },
        {
            field: "lastUpdate",
            headerName: t("common:grid.lastUpdate"),
            flex: 1.5,
            minWidth: 150,
            filterable: true,
            filterType: "date",
            renderCell: (params) => {
                return (React.createElement(GridCellStack, { topText: formatUtcToLocalDate(params.row.lastUpdate), bottomText: formatUtcToLocalTime(params.row.lastUpdate) }));
            },
        },
        {
            field: "actions",
            headerName: t("common:grid.actions"),
            minWidth: 240,
            filterable: false,
            headerAlign: "center",
            renderCell: (params) => (React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1, justifyContent: "end" }) },
                PermissionChecker.hasPermission(Permission.ACCESSPOINTS_DELETE) && (React.createElement(SIconButton, { colorVariant: "error", title: t("common:actions.delete"), size: "small", onClick: (e) => {
                        e.stopPropagation();
                        setApToDelete({
                            deviceId: params.row.deviceId,
                            name: params.row.name,
                        });
                        setDeleteDialogOpen(true);
                    } },
                    React.createElement(Icon, { icon: "mingcute:delete-2-line" }))),
                params.row.hasMaintenanceRequest && (React.createElement(SIconButton, { size: "small", title: t("common:actions.maintenance"), colorVariant: "primary", onClick: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setSelectedApForMaintenance({
                            apUid: params.row.deviceId,
                            apName: params.row.name,
                        });
                        setMaintenanceDialogOpen(true);
                    } },
                    React.createElement(MaintenanceIcon, { size: 24 }))),
                React.createElement(SButton, { variant: "soft", size: "medium", color: "primary", onClick: (e) => {
                        e.stopPropagation();
                        handleViewNodes(params.row.deviceId);
                    } }, t("common:actions.nodes")),
                PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT) && (React.createElement(SButton, { variant: "outlined", size: "medium", onClick: (e) => {
                        e.stopPropagation();
                        handleSettings(params.row.deviceId);
                    } }, t("common:actions.settings"))))),
        },
    ], [t, handleViewNodes, handleSettings]);
    // Client / location / category options of the filter dialog (filter-only fields, not grid columns). Loaded once;
    // the filtering itself runs on the server.
    const [clientOptions, setClientOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    useEffect(() => {
        let active = true;
        Promise.allSettled([
            clientsApi.getOptions(),
            clientsApi.getAllLocationOptions(),
            categoriesApi.getOptions("ap"),
        ]).then(([clients, locations, categories]) => {
            if (!active)
                return;
            if (clients.status === "fulfilled")
                setClientOptions(toOptions(clients.value.data.data));
            if (locations.status === "fulfilled")
                setLocationOptions(toOptions(locations.value.data.data));
            if (categories.status === "fulfilled")
                setCategoryOptions(toOptions(categories.value.data.data));
        });
        return () => {
            active = false;
        };
    }, []);
    const filterColumns = useMemo(() => [
        ...columns,
        {
            field: "clientIds",
            headerName: t("common:catalog.clients.client", "Client"),
            filterable: true,
            filterType: "select",
            filterOptions: clientOptions,
        },
        {
            field: "clientLocationIds",
            headerName: t("common:catalog.clients.location", "Location"),
            filterable: true,
            filterType: "select",
            filterOptions: locationOptions,
        },
        {
            field: "apCategoryIds",
            headerName: t("common:catalog.categories.title", "Categories"),
            filterable: true,
            filterType: "select",
            filterOptions: categoryOptions,
        },
    ], [columns, clientOptions, locationOptions, categoryOptions, t]);
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("") }),
        React.createElement(PageContent, null,
            isSuperAdmin && (React.createElement(Grid, { container: true, spacing: 1, mb: 1.5 }, statsCards.map((stat, index) => (React.createElement(Grid, { size: { xs: index === 2 ? 12 : 6, sm: 6, md: 4 }, key: stat.id },
                React.createElement(FCard, { variant: "stats", sx: { height: "100%", p: { xs: "0px" } }, title: stat.title, value: stat.value.toLocaleString(), selected: selectedCard === stat.id, onClick: () => handleCardClick(stat.id, stat.filterValue) })))))),
            React.createElement(GridFilters, { title: `${(_a = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _a !== void 0 ? _a : devices.length} ${t("common:dashboard.devices")}`, loading: loading, columns: filterColumns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange }),
            React.createElement(PrimaryGrid, { rows: loading ? [] : devices, columns: columns, loading: loading, heightVariant: "dashboard", checkboxSelection: false, autoHeight: true, paginationMode: "server", rowCount: (_b = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _b !== void 0 ? _b : 0, paginationModel: {
                    page: currentPageNumber - 1,
                    pageSize: currentPageSize,
                }, onPaginationModelChange: (model) => {
                    if (model.pageSize !== currentPageSize) {
                        handlePageSizeChange(model.pageSize);
                        handlePageChange(1);
                    }
                    else if (model.page + 1 !== currentPageNumber) {
                        handlePageChange(model.page + 1);
                    }
                }, pageSizeOptions: [25, 50], renderCard: (row) => (React.createElement(DashboardDeviceCard, { row: row, onViewNodes: handleViewNodes, onSettings: handleSettings, onDelete: (deviceId, name) => {
                        setApToDelete({ deviceId, name });
                        setDeleteDialogOpen(true);
                    }, onMaintenance: (deviceId, name) => {
                        setSelectedApForMaintenance({ apUid: deviceId, apName: name });
                        setMaintenanceDialogOpen(true);
                    }, showDelete: PermissionChecker.hasPermission(Permission.ACCESSPOINTS_DELETE), showSettings: PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT) })), onRowClick: (params, event) => {
                    // Don't navigate if clicking on the maintenance column
                    const target = event.target;
                    if (target.closest('[data-field="maintenance"]') ||
                        target.closest('[data-field="actions"]')) {
                        return;
                    }
                    handleViewNodes(params.row.deviceId);
                } })),
        React.createElement(MaintenanceRequestsDialog, { isOpen: maintenanceDialogOpen, onClose: handleMaintenanceDialogClose, apUid: (selectedApForMaintenance === null || selectedApForMaintenance === void 0 ? void 0 : selectedApForMaintenance.apUid) || "", apName: selectedApForMaintenance === null || selectedApForMaintenance === void 0 ? void 0 : selectedApForMaintenance.apName }),
        React.createElement(ConfirmDialog, { isOpen: deleteDialogOpen, onClose: handleDeleteDialogClose, onConfirm: handleConfirmDeleteAp, loading: deleteLoading, variant: "delete", actionText: t("common:actions.delete"), screenName: apToDelete
                ? `${t("common:actions.accessPoint")} "${apToDelete.name}"`
                : "", customMessage: deleteError !== null && deleteError !== void 0 ? deleteError : undefined })));
};
export default DashboardPage;
